import {
	DateRangePicker,
	DateRangePickerChangeEvent,
	SelectionRange,
	MultiViewCalendar,
	MultiViewCalendarProps,
} from "@progress/kendo-react-dateinputs";
import { Translate } from "../translate/translate.component";
import { Button } from "@mui/material";
import { useEffect, useState } from "react";
import "../custom-date-picker/custom-date-picker.css";

// Helper functions for consistent time boundaries
const setStartOfDay = (date: Date): Date => {
	const newDate = new Date(date);
	newDate.setHours(0, 0, 0, 0);
	return newDate;
};

const setEndOfDay = (date: Date): Date => {
	const newDate = new Date(date);
	newDate.setHours(23, 59, 59, 999); // 23:59:59.999
	return newDate;
};

// Generate dynamic year options based on current year
const generateYearOptions = () => {
	const currentYear = new Date().getFullYear();
	return [
		{ id: `year_${currentYear}`, label: currentYear.toString() },
		{ id: `year_${currentYear - 1}`, label: (currentYear - 1).toString() },
		{ id: `year_${currentYear - 2}`, label: (currentYear - 2).toString() },
	];
};

// Organized date range categories
const DATE_RANGE_CATEGORIES = {
	days: {
		label: "Tage",
		options: [
			{ id: "clear", label: "Auswahl aufheben" },
			{ id: "dayBeforeYesterday", label: "Vorgestern" },
			{ id: "yesterday", label: "Gestern" },
			{ id: "today", label: "Heute" },
		],
	},
	weeks: {
		label: "Wochen",
		options: [
			{ id: "lastWeek", label: "Letzte Woche" },
			{ id: "thisWeek", label: "Diese Woche" },
		],
	},
	months: {
		label: "Monate",
		options: [
			{ id: "lastMonth", label: "Letzter Monat" },
			{ id: "thisMonth", label: "Dieser Monat" },
		],
	},
	quarters: {
		label: "Quartale",
		options: (() => {
			const currentQuarter = Math.floor(new Date().getMonth() / 3) + 1;
			return [
				{
					id: "q1",
					label:
						currentQuarter === 1
							? "Q1 (Jan-März) (aktuell)"
							: "Q1 (Jan-März)",
				},
				{
					id: "q2",
					label:
						currentQuarter === 2
							? "Q2 (Apr-Jun) (aktuell)"
							: "Q2 (Apr-Jun)",
				},
				{
					id: "q3",
					label:
						currentQuarter === 3
							? "Q3 (Jul-Sep) (aktuell)"
							: "Q3 (Jul-Sep)",
				},
				{
					id: "q4",
					label:
						currentQuarter === 4
							? "Q4 (Okt-Dez) (aktuell)"
							: "Q4 (Okt-Dez)",
				},
			];
		})(),
	},
	semesters: {
		label: "Halbjahre",
		options: (() => {
			const currentSemester = new Date().getMonth() < 6 ? 1 : 2;
			return [
				{
					id: "firstSemester",
					label:
						currentSemester === 1
							? "1. Halbjahr (aktuell)"
							: "1. Halbjahr",
				},
				{
					id: "secondSemester",
					label:
						currentSemester === 2
							? "2. Halbjahr (aktuell)"
							: "2. Halbjahr",
				},
			];
		})(),
	},
	years: {
		label: "Jahre",
		options: (() => {
			const currentYear = new Date().getFullYear();
			const yearOptions = generateYearOptions();
			return yearOptions.map((year) => ({
				...year,
				label:
					year.label === currentYear.toString()
						? `${year.label} (aktuell)`
						: year.label,
			}));
		})(),
	},
};
function datesEqual(date1: Date, date2: Date): boolean {
	return (
		date1.getFullYear() === date2.getFullYear() &&
		date1.getMonth() === date2.getMonth() &&
		date1.getDate() === date2.getDate()
	);
}

function getDateRangeFromValue(startDate: Date, endDate: Date): string | null {
	const currentDate = new Date();
	const currentYear = currentDate.getFullYear();
	const currentMonth = currentDate.getMonth();

	// Helper to check if dates match (ignoring time for more flexible matching)
	const matchesDateRange = (start: Date, end: Date): boolean => {
		return datesEqual(startDate, start) && datesEqual(endDate, end);
	};

	if (!startDate || !endDate) {
		return "clear";
	}
	// Check days
	const today = new Date();
	if (
		matchesDateRange(
			new Date(today.getFullYear(), today.getMonth(), today.getDate()),
			new Date(today.getFullYear(), today.getMonth(), today.getDate())
		)
	) {
		return "today";
	}

	const yesterday = new Date();
	yesterday.setDate(yesterday.getDate() - 1);
	if (
		matchesDateRange(
			new Date(
				yesterday.getFullYear(),
				yesterday.getMonth(),
				yesterday.getDate()
			),
			new Date(
				yesterday.getFullYear(),
				yesterday.getMonth(),
				yesterday.getDate()
			)
		)
	) {
		return "yesterday";
	}

	const dayBeforeYesterday = new Date();
	dayBeforeYesterday.setDate(dayBeforeYesterday.getDate() - 2);
	if (
		matchesDateRange(
			new Date(
				dayBeforeYesterday.getFullYear(),
				dayBeforeYesterday.getMonth(),
				dayBeforeYesterday.getDate()
			),
			new Date(
				dayBeforeYesterday.getFullYear(),
				dayBeforeYesterday.getMonth(),
				dayBeforeYesterday.getDate()
			)
		)
	) {
		return "dayBeforeYesterday";
	}

	// Check weeks
	const thisWeekStart = (() => {
		const today = new Date();
		const dayOfWeek = today.getDay();
		const monday = new Date(today);
		monday.setDate(today.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
		return new Date(
			monday.getFullYear(),
			monday.getMonth(),
			monday.getDate()
		);
	})();
	if (
		datesEqual(startDate, thisWeekStart) &&
		datesEqual(
			endDate,
			new Date(today.getFullYear(), today.getMonth(), today.getDate())
		)
	) {
		return "thisWeek";
	}

	const lastWeekStart = (() => {
		const lastMonday = new Date();
		const currentDay = lastMonday.getDay();
		lastMonday.setDate(
			lastMonday.getDate() - (currentDay === 0 ? 6 : currentDay - 1) - 7
		);
		return new Date(
			lastMonday.getFullYear(),
			lastMonday.getMonth(),
			lastMonday.getDate()
		);
	})();
	const lastWeekEnd = new Date(lastWeekStart);
	lastWeekEnd.setDate(lastWeekStart.getDate() + 6);
	if (
		datesEqual(startDate, lastWeekStart) &&
		datesEqual(endDate, lastWeekEnd)
	) {
		return "lastWeek";
	}

	// Check months (more flexible matching)
	const thisMonthStart = new Date(currentYear, currentMonth, 1);
	const thisMonthCurrentEnd = new Date(
		currentYear,
		currentMonth,
		currentDate.getDate()
	);

	if (
		datesEqual(startDate, thisMonthStart) &&
		(datesEqual(endDate, thisMonthCurrentEnd) ||
			(endDate.getHours() >= 21 &&
				datesEqual(endDate, thisMonthCurrentEnd)))
	) {
		return "thisMonth";
	}

	// Check last month
	const lastMonthYear = currentMonth === 0 ? currentYear - 1 : currentYear;
	const lastMonth = currentMonth === 0 ? 11 : currentMonth - 1;
	const lastMonthStart = new Date(lastMonthYear, lastMonth, 1);
	const lastMonthEnd = new Date(lastMonthYear, lastMonth + 1, 0);
	if (
		datesEqual(startDate, lastMonthStart) &&
		datesEqual(endDate, lastMonthEnd)
	) {
		return "lastMonth";
	}

	// Check specific quarters (Q1, Q2, Q3, Q4)
	const q1Start = new Date(currentYear, 0, 1); // January 1
	const q1End = new Date(currentYear, 2, 31); // March 31
	if (datesEqual(startDate, q1Start) && datesEqual(endDate, q1End)) {
		return "q1";
	}

	const q2Start = new Date(currentYear, 3, 1); // April 1
	const q2End = new Date(currentYear, 5, 30); // June 30
	if (datesEqual(startDate, q2Start) && datesEqual(endDate, q2End)) {
		return "q2";
	}

	const q3Start = new Date(currentYear, 6, 1); // July 1
	const q3End = new Date(currentYear, 8, 30); // September 30
	if (datesEqual(startDate, q3Start) && datesEqual(endDate, q3End)) {
		return "q3";
	}

	const q4Start = new Date(currentYear, 9, 1); // October 1
	const q4End = new Date(currentYear, 11, 31); // December 31
	if (datesEqual(startDate, q4Start) && datesEqual(endDate, q4End)) {
		return "q4";
	}

	// Check semesters
	const firstSemesterStart = new Date(currentYear, 0, 1); // Jan 1
	const firstSemesterEnd = new Date(currentYear, 5, 30); // June 30
	if (
		datesEqual(startDate, firstSemesterStart) &&
		datesEqual(endDate, firstSemesterEnd)
	) {
		return "firstSemester";
	}

	const secondSemesterStart = new Date(currentYear, 6, 1); // July 1
	const secondSemesterEnd = new Date(currentYear, 11, 31); // Dec 31
	if (
		datesEqual(startDate, secondSemesterStart) &&
		datesEqual(endDate, secondSemesterEnd)
	) {
		return "secondSemester";
	}

	// Check specific years
	for (let year = currentYear - 2; year <= currentYear; year++) {
		const yearStart = new Date(year, 0, 1);
		const yearEnd = new Date(year, 11, 31);
		if (datesEqual(startDate, yearStart) && datesEqual(endDate, yearEnd)) {
			return `year_${year}`;
		}
	}

	// Legacy checks for backwards compatibility
	// Check this quarter
	const quarterStart = Math.floor(currentMonth / 3) * 3;
	const thisQuarterStart = new Date(currentYear, quarterStart, 1);
	if (
		datesEqual(startDate, thisQuarterStart) &&
		(datesEqual(endDate, thisMonthCurrentEnd) ||
			(endDate.getHours() >= 22 &&
				datesEqual(endDate, thisMonthCurrentEnd)))
	) {
		return "thisQuarter";
	}

	// Check last quarter
	const lastQ = Math.floor(currentMonth / 3) - 1;
	const lastQuarterYear = lastQ < 0 ? currentYear - 1 : currentYear;
	const lastQuarterStartMonth = lastQ < 0 ? 9 : lastQ * 3;
	const lastQuarterStart = new Date(
		lastQuarterYear,
		lastQuarterStartMonth,
		1
	);
	const lastQuarterEnd = new Date(
		lastQuarterYear,
		lastQuarterStartMonth + 3,
		0
	);
	if (
		datesEqual(startDate, lastQuarterStart) &&
		datesEqual(endDate, lastQuarterEnd)
	) {
		return "lastQuarter";
	}

	// Check this year
	const thisYearStart = new Date(currentYear, 0, 1);
	if (
		datesEqual(startDate, thisYearStart) &&
		(datesEqual(endDate, thisMonthCurrentEnd) ||
			(endDate.getHours() >= 23 &&
				datesEqual(endDate, thisMonthCurrentEnd)))
	) {
		return "thisYear";
	}

	// Check last year
	const lastYearStart = new Date(currentYear - 1, 0, 1);
	const lastYearEnd = new Date(currentYear - 1, 11, 31);
	if (
		datesEqual(startDate, lastYearStart) &&
		datesEqual(endDate, lastYearEnd)
	) {
		return "lastYear";
	}

	// Check before last year
	const beforeLastYearStart = new Date(currentYear - 2, 0, 1);
	const beforeLastYearEnd = new Date(currentYear - 2, 11, 31);
	if (
		datesEqual(startDate, beforeLastYearStart) &&
		datesEqual(endDate, beforeLastYearEnd)
	) {
		return "beforeLastYear";
	}

	return null; // Custom or unrecognized date range
}
function getDateRange(option: string) {
	const currentDate = new Date();
	const currentYear = currentDate.getFullYear();
	const currentMonth = currentDate.getMonth();
	let startDate: Date;
	let endDate: Date;

	switch (option) {
		case "clear":
			return null; // This will clear the selection
		case "today":
			startDate = setStartOfDay(new Date());
			endDate = setEndOfDay(new Date());
			break;
		case "yesterday": {
			const yesterday = new Date();
			yesterday.setDate(yesterday.getDate() - 1);
			startDate = setStartOfDay(yesterday);
			endDate = setEndOfDay(yesterday);
			break;
		}
		case "dayBeforeYesterday": {
			const dayBeforeYesterday = new Date();
			dayBeforeYesterday.setDate(dayBeforeYesterday.getDate() - 2);
			startDate = setStartOfDay(dayBeforeYesterday);
			endDate = setEndOfDay(dayBeforeYesterday);
			break;
		}
		// Weeks
		case "thisWeek": {
			const today = new Date();
			const dayOfWeek = today.getDay();
			const monday = new Date(today);
			monday.setDate(
				today.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1)
			);
			startDate = setStartOfDay(monday);
			endDate = setEndOfDay(today);
			break;
		}
		case "lastWeek": {
			const lastMonday = new Date();
			const currentDay = lastMonday.getDay();
			lastMonday.setDate(
				lastMonday.getDate() -
					(currentDay === 0 ? 6 : currentDay - 1) -
					7
			);
			const lastSunday = new Date(lastMonday);
			lastSunday.setDate(lastMonday.getDate() + 6);
			startDate = setStartOfDay(lastMonday);
			endDate = setEndOfDay(lastSunday);
			break;
		}
		// Months
		case "thisMonth":
			startDate = setStartOfDay(new Date(currentYear, currentMonth, 1));
			endDate = setEndOfDay(
				new Date(currentYear, currentMonth, currentDate.getDate())
			);
			break;
		case "lastMonth": {
			const lastMonthYear =
				currentMonth === 0 ? currentYear - 1 : currentYear;
			const lastMonth = currentMonth === 0 ? 11 : currentMonth - 1;
			startDate = setStartOfDay(new Date(lastMonthYear, lastMonth, 1));
			endDate = setEndOfDay(new Date(lastMonthYear, lastMonth + 1, 0));
			break;
		}
		// Quarters - Fixed quarters
		case "q1":
			startDate = setStartOfDay(new Date(currentYear, 0, 1)); // January 1
			endDate = setEndOfDay(new Date(currentYear, 2, 31)); // March 31
			break;
		case "q2":
			startDate = setStartOfDay(new Date(currentYear, 3, 1)); // April 1
			endDate = setEndOfDay(new Date(currentYear, 5, 30)); // June 30
			break;
		case "q3":
			startDate = setStartOfDay(new Date(currentYear, 6, 1)); // July 1
			endDate = setEndOfDay(new Date(currentYear, 8, 30)); // September 30
			break;
		case "q4":
			startDate = setStartOfDay(new Date(currentYear, 9, 1)); // October 1
			endDate = setEndOfDay(new Date(currentYear, 11, 31)); // December 31
			break;

		// Semesters
		case "firstSemester":
			startDate = setStartOfDay(new Date(currentYear, 0, 1)); // Jan 1
			endDate = setEndOfDay(new Date(currentYear, 5, 30)); // June 30
			break;
		case "secondSemester":
			startDate = setStartOfDay(new Date(currentYear, 6, 1)); // July 1
			endDate = setEndOfDay(new Date(currentYear, 11, 31)); // Dec 31
			break;

		// Legacy compatibility cases
		case "thisQuarter": {
			const quarterStart = Math.floor(currentMonth / 3) * 3;
			startDate = setStartOfDay(new Date(currentYear, quarterStart, 1));
			endDate = setEndOfDay(new Date());
			break;
		}
		case "lastQuarter": {
			const lastQ = Math.floor(currentMonth / 3) - 1;
			const lastQuarterYear = lastQ < 0 ? currentYear - 1 : currentYear;
			const lastQuarterStartMonth = lastQ < 0 ? 9 : lastQ * 3;
			const lastQuarterEndMonth = lastQuarterStartMonth + 3;
			startDate = setStartOfDay(
				new Date(lastQuarterYear, lastQuarterStartMonth, 1)
			);
			endDate = setEndOfDay(
				new Date(lastQuarterYear, lastQuarterEndMonth, 0)
			);
			break;
		}
		case "thisYear": {
			startDate = setStartOfDay(new Date(currentYear, 0, 1));
			endDate = setEndOfDay(new Date());
			break;
		}
		case "lastYear": {
			startDate = setStartOfDay(new Date(currentYear - 1, 0, 1));
			endDate = setEndOfDay(new Date(currentYear - 1, 11, 31));
			break;
		}
		case "beforeLastYear": {
			startDate = setStartOfDay(new Date(currentYear - 2, 0, 1));
			endDate = setEndOfDay(new Date(currentYear - 2, 11, 31));
			break;
		}
		// Specific years - handle any year_YYYY format
		default:
			if (option.startsWith("year_")) {
				const year = parseInt(option.split("_")[1]);
				if (!isNaN(year)) {
					startDate = setStartOfDay(new Date(year, 0, 1));
					endDate = setEndOfDay(new Date(year, 11, 31));
					break;
				}
			}
			return null; // Invalid option
	}
	return { startDate, endDate };
}

const CustomCalendar = (props: MultiViewCalendarProps) => {
	const { onChange, value } = props;
	const [selectedDateRange, setSelectedDateRange] = useState<string>("");

	const handleDateRangeChange = (optionId: string) => {
		setSelectedDateRange(optionId);
		const dateRange = getDateRange(optionId);

		if (onChange) {
			if (optionId === "clear" || !dateRange) {
				// Clear the selection
				onChange({
					value: {
						start: null,
						end: null,
					},
					// @ts-expect-error - This is a synthetic event and it's not being used
					syntheticEvent: null,
				});
			} else {
				// Set the date range
				onChange({
					value: {
						start: dateRange.startDate,
						end: dateRange.endDate,
					},
					// @ts-expect-error - This is a synthetic event and it's not being used
					syntheticEvent: null,
				});
			}
		}
	};

	const handleClick = (
		e: React.MouseEvent<HTMLDivElement | HTMLButtonElement>
	) => {
		e.stopPropagation();
		e.preventDefault();
	};

	useEffect(() => {
		// @ts-expect-error Value is of type SelectionRange
		if (!value?.start || !value?.end) {
			setSelectedDateRange("");
			return;
		}
		// @ts-expect-error Value is of type SelectionRange
		const dateRange = getDateRangeFromValue(value?.start, value?.end);
		if (dateRange) {
			setSelectedDateRange(dateRange);
		} else {
			setSelectedDateRange("");
		}
	}, [value]);

	const leftCategories = ["days", "weeks", "months"];
	const rightCategories = ["quarters", "semesters", "years"];

	return (
		<div
			style={{
				display: "flex",
				flexDirection: "column",
				maxWidth: 700,
				height: "100%",
			}}
			onClick={(e) => {
				e.stopPropagation();
				e.preventDefault();
			}}
			onMouseDown={(e) => {
				e.stopPropagation();
				e.preventDefault();
			}}
			onMouseUp={(e) => {
				e.stopPropagation();
				e.preventDefault();
			}}
		>
			<div
				style={{
					display: "flex",
					padding: "12px 16px 16px 16px",
					gap: "20px",
				}}
				onClick={(e) => {
					handleClick(e);
				}}
				onMouseDown={(e) => {
					handleClick(e);
				}}
				onMouseUp={(e) => {
					handleClick(e);
				}}
			>
				{/* Left column - Days, Weeks, Months */}
				<div
					style={{
						flex: 1,
						display: "flex",
						flexDirection: "column",
					}}
					onClick={(e) => {
						handleClick(e);
					}}
					onMouseDown={(e) => {
						handleClick(e);
					}}
					onMouseUp={(e) => {
						handleClick(e);
					}}
				>
					{leftCategories.map((categoryKey, categoryIndex) => {
						const category =
							DATE_RANGE_CATEGORIES[
								categoryKey as keyof typeof DATE_RANGE_CATEGORIES
							];
						return (
							<div
								key={categoryKey}
								onClick={(e) => {
									handleClick(e);
								}}
								onMouseDown={(e) => {
									handleClick(e);
								}}
								onMouseUp={(e) => {
									handleClick(e);
								}}
							>
								{category.options.map((option) => (
									<Button
										key={option.id}
										style={{
											width: "100%",
											textAlign: "left",
											justifyContent: "flex-start",
											cursor: "pointer",
											padding: "4px 8px",
											fontSize: 14,
											backgroundColor:
												selectedDateRange === option.id
													? "#2288da"
													: "transparent",
											color:
												selectedDateRange === option.id
													? "white"
													: option.id === "clear"
														? "#dc2626" // Red color for clear option
														: "#374151",
											marginBottom: 1,
											minHeight: 28,
											border: "none",
											borderRadius: "4px",
											fontWeight:
												selectedDateRange === option.id
													? "500"
													: "400",
											transition: "all 0.2s ease",
										}}
										onClick={(e) => {
											handleClick(e);
											handleDateRangeChange(option.id);
										}}
										onMouseDown={(e) => {
											handleClick(e);
										}}
										onMouseUp={(e) => {
											handleClick(e);
										}}
									>
										{option.label}
									</Button>
								))}
								{categoryIndex < leftCategories.length - 1 && (
									<div
										style={{
											height: "1px",
											backgroundColor: "#f0f0f0",
											margin: "6px 0",
										}}
									/>
								)}
							</div>
						);
					})}
				</div>

				{/* Right column - Quarters, Semesters, Years */}
				<div
					style={{
						flex: 1,
						display: "flex",
						flexDirection: "column",
					}}
					onClick={(e) => {
						handleClick(e);
					}}
					onMouseDown={(e) => {
						handleClick(e);
					}}
					onMouseUp={(e) => {
						handleClick(e);
					}}
				>
					{rightCategories.map((categoryKey, categoryIndex) => {
						const category =
							DATE_RANGE_CATEGORIES[
								categoryKey as keyof typeof DATE_RANGE_CATEGORIES
							];
						return (
							<div
								key={categoryKey}
								onClick={(e) => {
									handleClick(e);
								}}
								onMouseDown={(e) => {
									handleClick(e);
								}}
								onMouseUp={(e) => {
									handleClick(e);
								}}
							>
								{category.options.map((option) => (
									<Button
										key={option.id}
										style={{
											width: "100%",
											textAlign: "left",
											justifyContent: "flex-start",
											cursor: "pointer",
											padding: "4px 8px",
											fontSize: 14,
											backgroundColor:
												selectedDateRange === option.id
													? "#2288da"
													: "transparent",
											color:
												selectedDateRange === option.id
													? "white"
													: "#374151",
											marginBottom: 1,
											minHeight: 28,
											border: "none",
											borderRadius: "4px",
											fontWeight:
												selectedDateRange === option.id
													? "500"
													: "400",
											transition: "all 0.2s ease",
										}}
										onClick={(e) => {
											handleClick(e);
											handleDateRangeChange(option.id);
										}}
										onMouseDown={(e) => {
											handleClick(e);
										}}
										onMouseUp={(e) => {
											handleClick(e);
										}}
									>
										{option.label}
									</Button>
								))}
								{categoryIndex < rightCategories.length - 1 && (
									<div
										style={{
											height: "1px",
											backgroundColor: "#f0f0f0",
											margin: "6px 0",
										}}
									/>
								)}
							</div>
						);
					})}
				</div>
			</div>

			{/* Calendar below */}
			<div
				style={{
					padding: "0 16px 16px 16px",
				}}
				onClick={(e) => {
					handleClick(e);
				}}
				onMouseDown={(e) => {
					handleClick(e);
				}}
				onMouseUp={(e) => {
					handleClick(e);
				}}
			>
				<MultiViewCalendar {...props} views={3} />
			</div>
		</div>
	);
};

export const CustomDateRangePicker: React.FC<{
	dateVal: SelectionRange;
	handleDateChange: (value: SelectionRange) => void;
}> = ({ dateVal, handleDateChange }) => {
	const [isOpen, setIsOpen] = useState(false);

	return (
		<Translate>
			<DateRangePicker
				format={"dd.MM.yyyy"}
				startDateInputSettings={{
					label: "",
					formatPlaceholder: {
						day: "Tag",
						month: "Monat",
						year: "Jahr",
					},
				}}
				endDateInputSettings={{
					label: "",
					formatPlaceholder: {
						day: "Tag",
						month: "Monat",
						year: "Jahr",
					},
				}}
				calendar={CustomCalendar}
				style={{
					width: 360,
				}}
				value={dateVal}
				show={isOpen}
				valid={true}
				onOpen={() => setIsOpen(true)}
				onClose={() => setIsOpen(false)}
				onChange={(event: DateRangePickerChangeEvent) => {
					handleDateChange(event.value);
					// Only close if we have both start and end dates
					if (
						(event.value.start && event.value.end) ||
						(event.value.start === null && event.value.end === null)
					) {
						setIsOpen(false);
					}
				}}
			/>
		</Translate>
	);
};
